import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import StopIcon from "@mui/icons-material/Stop";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { LoadingBox } from "@components/LoadingBox";
import { useLiveMatch } from "@hooks/useLiveMatch";
import { TeamId } from "@/constants/teams";
import { PAGE_CONTAINER_SX } from "@/constants/layout";

const TEAM_IDS = [TeamId.Red, TeamId.Blue] as const;

export default function LiveMatch() {
  const { t } = useTranslation();
  const {
    match,
    events,
    score,
    loading,
    goalDialog,
    setGoalDialog,
    selectedScorer,
    setSelectedScorer,
    selectedAssister,
    setSelectedAssister,
    isOwnGoal,
    setIsOwnGoal,
    endOpen,
    setEndOpen,
    teamPlayers,
    resetGoalForm,
    handleRecordGoal,
    handleDeleteEvent,
    handleEndMatch,
  } = useLiveMatch();

  if (loading) {
    return <LoadingBox />;
  }

  if (!match) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography>{t("match.notFound")}</Typography>
      </Box>
    );
  }

  const isEnded = match.isEnded;

  return (
    <Box sx={PAGE_CONTAINER_SX}>
      {/* Scoreboard */}
      <Paper
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 2, sm: 4 },
          p: 3,
          mb: 3,
          borderRadius: "8px",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Chip label={t("teams.redTeam")} color="error" sx={{ mb: 1 }} />
          <Typography
            variant="h2"
            sx={{
              fontWeight: "bold",
              color: "error.main",
            }}
          >
            {score.red}
          </Typography>
        </Box>
        <Typography
          variant="h4"
          sx={{
            color: "text.secondary",
          }}
        >
          -
        </Typography>
        <Box sx={{ textAlign: "center" }}>
          <Chip label={t("teams.blueTeam")} color="primary" sx={{ mb: 1 }} />
          <Typography
            variant="h2"
            sx={{
              fontWeight: "bold",
              color: "primary.main",
            }}
          >
            {score.blue}
          </Typography>
        </Box>
      </Paper>
      {/* Team panels */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mb: 3,
        }}
      >
        {TEAM_IDS.map((teamId) => (
          <Paper key={teamId} sx={{ p: 2 }}>
            <Chip
              label={
                teamId === TeamId.Red ? t("teams.redTeam") : t("teams.blueTeam")
              }
              color={teamId === TeamId.Red ? "error" : "primary"}
              sx={{ mb: 1 }}
            />
            <List dense>
              {teamPlayers(teamId).map((mp) => (
                <ListItem key={mp.playerId} disablePadding>
                  <ListItemButton
                    disabled={isEnded}
                    onClick={() => {
                      setGoalDialog({ teamId });
                      setSelectedScorer(mp.playerId);
                    }}
                  >
                    <SportsSoccerIcon
                      sx={{ mr: 1, fontSize: 18, color: "text.secondary" }}
                    />
                    <ListItemText primary={mp.player?.name ?? mp.playerId} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        ))}
      </Box>
      {/* Event log */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 600,
          }}
        >
          {t("match.eventLog")}
        </Typography>
        {events.length === 0 ? (
          <Typography
            sx={{
              color: "text.secondary",
              py: 2,
              textAlign: "center",
            }}
          >
            {t("match.noEvents")}
          </Typography>
        ) : (
          <List dense>
            {events.map((e) => (
              <ListItem
                key={e.id}
                secondaryAction={
                  !isEnded && (
                    <IconButton
                      size="small"
                      onClick={() => void handleDeleteEvent(e.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <Chip
                  label={e.teamId === TeamId.Red ? "R" : "B"}
                  size="small"
                  color={e.teamId === TeamId.Red ? "error" : "primary"}
                  sx={{ mr: 1, minWidth: 28 }}
                />
                <ListItemText
                  primary={
                    e.isOwnGoal
                      ? `${t("match.ownGoal")} - ${e.scorer?.name ?? ""}`
                      : `${e.scorer?.name ?? ""}${e.assister ? ` (${t("match.assist")}: ${e.assister.name})` : ""}`
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      {!isEnded && (
        <Button
          variant="contained"
          color="error"
          startIcon={<StopIcon />}
          onClick={() => setEndOpen(true)}
          fullWidth
          size="large"
        >
          {t("match.endMatch")}
        </Button>
      )}
      {/* Goal Dialog */}
      <Dialog
        open={!!goalDialog}
        onClose={resetGoalForm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("match.recordGoal")}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("match.scorer")}
          </Typography>
          <List dense>
            {goalDialog &&
              teamPlayers(goalDialog.teamId).map((mp) => (
                <ListItemButton
                  key={mp.playerId}
                  selected={selectedScorer === mp.playerId}
                  onClick={() => setSelectedScorer(mp.playerId)}
                >
                  <ListItemText primary={mp.player?.name} />
                </ListItemButton>
              ))}
          </List>

          <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
            {t("match.assister")} ({t("common.optional")})
          </Typography>
          <List dense>
            <ListItemButton
              selected={selectedAssister === null}
              onClick={() => setSelectedAssister(null)}
            >
              <ListItemText primary={`- ${t("match.noAssist")} -`} />
            </ListItemButton>
            {goalDialog &&
              teamPlayers(goalDialog.teamId)
                .filter((mp) => mp.playerId !== selectedScorer)
                .map((mp) => (
                  <ListItemButton
                    key={mp.playerId}
                    selected={selectedAssister === mp.playerId}
                    onClick={() => setSelectedAssister(mp.playerId)}
                  >
                    <ListItemText primary={mp.player?.name} />
                  </ListItemButton>
                ))}
          </List>

          <FormControlLabel
            control={
              <Checkbox
                checked={isOwnGoal}
                onChange={(ev) => setIsOwnGoal(ev.target.checked)}
              />
            }
            label={t("match.ownGoal")}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetGoalForm}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => void handleRecordGoal()}
            disabled={!selectedScorer}
          >
            {t("match.recordGoal")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={endOpen}
        title={t("match.endMatch")}
        message={t("match.endConfirm")}
        onConfirm={() => void handleEndMatch()}
        onCancel={() => setEndOpen(false)}
      />
    </Box>
  );
}
